import { ORGANISATION_EMAIL_DOMAINS_MAX, ORGANISATION_NAME_MAX, ORGANISATION_NAME_MIN, type OrganisationKind, type OrgRole } from "@sagot/constants";
import type { Activity, LeadDetail } from "../../engage/leads/lead-types";
import { DOMAIN_PATTERN } from "../organisations/organisation-constants";
import { LEAD_COMPANY_FIELDS, LEAD_CONVERTED_ACTIVITY, LEAD_DOMAIN_SEPARATOR } from "./lead-convert-constants";
import type { LeadConvertBody, LeadProposal } from "./lead-convert-types";

export interface ConvertForm {
  organisationName: string;
  kind: OrganisationKind;
  /** Optional e-mail domains of the organisation, separated by spaces, commas or semicolons. */
  domains: string;
  contactName: string;
  contactEmail: string;
  jobTitle: string;
  orgRole: OrgRole;
}

export type ConvertProblem = "organisationName" | "domains" | "contactName" | "contactEmail";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function companyOf(fields: LeadDetail["custom_fields"]): string {
  for (const key of LEAD_COMPANY_FIELDS) {
    const value = fields[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return "";
}

/** The form starts from the lead: the contact is the person who asked, the organisation name from a company field if the lead has one. */
export function initialConvertForm(lead: Pick<LeadDetail, "name" | "email" | "custom_fields">): ConvertForm {
  return {
    organisationName: companyOf(lead.custom_fields),
    kind: "customer",
    domains: "",
    contactName: lead.name,
    contactEmail: lead.email ?? "",
    jobTitle: "",
    orgRole: "org_admin",
  };
}

export function domainsOf(text: string): string[] {
  return text
    .split(LEAD_DOMAIN_SEPARATOR)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part !== "");
}

export function convertProblems(form: ConvertForm): ConvertProblem[] {
  const problems: ConvertProblem[] = [];
  const name = form.organisationName.trim();
  if (name.length < ORGANISATION_NAME_MIN || name.length > ORGANISATION_NAME_MAX) problems.push("organisationName");
  const domains = domainsOf(form.domains);
  if (domains.length > ORGANISATION_EMAIL_DOMAINS_MAX || domains.some((domain) => !DOMAIN_PATTERN.test(domain)) || new Set(domains).size !== domains.length) problems.push("domains");
  if (form.contactName.trim() === "") problems.push("contactName");
  if (!EMAIL_PATTERN.test(form.contactEmail.trim())) problems.push("contactEmail");
  return problems;
}

export function convertBody(form: ConvertForm, linkExisting: boolean): LeadConvertBody {
  return {
    organisation_name: form.organisationName.trim(),
    kind: form.kind,
    email_domains: domainsOf(form.domains),
    contact_name: form.contactName.trim(),
    contact_email: form.contactEmail.trim(),
    job_title: form.jobTitle.trim() === "" ? null : form.jobTitle.trim(),
    org_role: form.orgRole,
    link_existing: linkExisting,
  };
}

/** Where the lead went, read from its timeline: the conversion writes one activity holding both ids. */
export function conversionOf(activities: readonly Activity[]): { organisationId: string; contactId: string | null; at: string } | null {
  const found = activities.find((activity) => activity.type === LEAD_CONVERTED_ACTIVITY);
  const organisationId = found?.data.organisation_id;
  if (found === undefined || typeof organisationId !== "string") return null;
  const contactId = found.data.contact_id;
  return { organisationId, contactId: typeof contactId === "string" ? contactId : null, at: found.created_at };
}

/** A duplicate stops the conversion unless the person chooses to link the lead to what exists. */
export function hasDuplicates(proposal: LeadProposal): boolean {
  return proposal.duplicates.length > 0;
}
